import { useState, useEffect } from 'react';
import { api } from '../../utils/api';
import { parseEat } from '../../utils/datetime';
import { Button, StatusBadge, Modal, useToast } from '../shared/UI';

function normalizeCommentMode(mode) {
  if (mode === 'mandatory' || mode === 'required') return 'mandatory';
  if (mode === 'optional') return 'optional';
  return 'disabled';
}

function ActiveTicketPanel({ ticket, onClose, onCall, commentMode }) {
  const [comment, setComment] = useState('');
  const [closing, setClosing] = useState(false);
  const [showClose, setShowClose] = useState(false);
  const mode = normalizeCommentMode(commentMode);

  const close = async () => {
    if (mode === 'mandatory' && !comment.trim()) return;
    try {
      setClosing(true);
      await api.post(`/api/tickets/${ticket.id}/close`, { comment });
      setShowClose(false);
      onClose();
    } catch (err) {
      alert(err.error || err.message || 'Failed to close ticket');
    } finally {
      setClosing(false);
    }
  };

  return (
    <div style={{ background: 'linear-gradient(135deg, var(--cobalt) 0%, var(--cobalt-light) 100%)', borderRadius: 20, padding: '28px 32px', color: '#fff', boxShadow: '0 8px 32px rgba(21,101,192,0.4)' }}>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', opacity: 0.75, fontFamily: 'var(--font-display)' }}>Now Serving</div>
      </div>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 52, fontWeight: 800, letterSpacing: '0.05em', marginBottom: 12, textShadow: '0 2px 12px rgba(0,0,0,0.3)' }}>{ticket.ticket_number}</div>
      <div style={{ fontSize: 16, opacity: 0.85, marginBottom: 4 }}>{ticket.service_name}</div>
      {ticket.customer_name && <div style={{ fontSize: 14, opacity: 0.7 }}>👤 {ticket.customer_name}</div>}
      <div style={{ marginTop: 24, display: 'flex', gap: 12 }}>
        <button onClick={() => onCall(ticket)}
          style={{ flex: 1, padding: '14px', fontSize: 16, borderRadius: 12, border: '2px solid rgba(255,255,255,0.4)', background: 'rgba(255,255,255,0.15)', color: '#fff', cursor: 'pointer', fontFamily: 'var(--font-display)', fontWeight: 700, backdropFilter: 'blur(4px)' }}>
          📢 Recall
        </button>
        <button onClick={() => setShowClose(true)}
          style={{ flex: 1, padding: '14px', fontSize: 16, borderRadius: 12, border: 'none', background: '#fff', color: 'var(--cobalt)', cursor: 'pointer', fontFamily: 'var(--font-display)', fontWeight: 700 }}>
          ✓ Close Ticket
        </button>
      </div>

      <Modal open={showClose} onClose={() => setShowClose(false)} title="Close Ticket">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ fontSize: 14, color: 'var(--gray-700)' }}>
            Close ticket <strong style={{ fontFamily: 'var(--font-mono)' }}>{ticket.ticket_number}</strong> for {ticket.customer_name || 'customer'}?
          </div>
          {mode !== 'disabled' && (
            <div>
              <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--gray-700)', fontFamily: 'var(--font-display)', display: 'block', marginBottom: 6 }}>
                Agent Comment {mode === 'mandatory' && <span style={{ color: 'var(--red)' }}>*</span>}
              </label>
              <textarea value={comment} onChange={e => setComment(e.target.value)} rows={3}
                placeholder={mode === 'mandatory' ? 'Comment is required for this service…' : 'Optional comment…'}
                style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1.5px solid var(--gray-100)', fontSize: 14, outline: 'none', resize: 'vertical', fontFamily: 'var(--font-body)' }} />
              {mode === 'mandatory' && !comment.trim() && (
                <p style={{ fontSize: 12, color: 'var(--red)', marginTop: 4 }}>A comment is required before closing.</p>
              )}
            </div>
          )}
          <div style={{ display: 'flex', gap: 10 }}>
            <Button onClick={() => setShowClose(false)} variant="ghost" style={{ flex: 1 }}>Cancel</Button>
            <Button onClick={close} variant="success" loading={closing} disabled={mode === 'mandatory' && !comment.trim()} style={{ flex: 1 }}>
              Confirm Close
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function TicketCard({ ticket, isNext, onCall, onSkip, onOpen, isOpen }) {
  const created = parseEat(ticket.created_at);
  const wait = created ? Math.floor((Date.now() - created.getTime()) / 60000) : 0;
  return (
    <div style={{
      padding: '16px 20px', borderRadius: 14, border: `2px solid ${isOpen ? 'var(--cobalt)' : isNext ? 'rgba(21,101,192,0.3)' : 'var(--gray-100)'}`,
      background: isOpen ? 'var(--ice)' : '#fff', transition: 'var(--transition)', animation: 'fadeIn 0.25s ease',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 20, color: 'var(--cobalt)' }}>{ticket.ticket_number}</span>
        <StatusBadge status={ticket.status} />
      </div>
      <div style={{ fontSize: 13, color: 'var(--navy)', marginBottom: 4, fontWeight: 500 }}>{ticket.service_name}</div>
      <div style={{ display: 'flex', gap: 12, fontSize: 12, color: 'var(--gray-500)', marginBottom: 14 }}>
        {ticket.customer_name && <span>👤 {ticket.customer_name}</span>}
        <span>⏱ {wait}m waiting</span>
        {ticket.call_count > 0 && <span style={{ color: ticket.call_count >= 3 ? 'var(--red)' : 'var(--amber)', fontWeight: 600 }}>📢 Called {ticket.call_count}×</span>}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        {ticket.status === 'Waiting' && (
          <>
            <Button onClick={() => onCall(ticket)} variant="primary" size="sm" style={{ flex: 1 }}>📢 Call</Button>
            {ticket.call_count >= 3 && (
              <Button onClick={() => onSkip(ticket)} variant="danger" size="sm">Skip</Button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default function CounterApp() {
  const [counters, setCounters] = useState([]);
  const [selectedCounter, setSelectedCounter] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [openTicket, setOpenTicket] = useState(null);
  const [calling, setCalling] = useState(false);
  const { show, ToastContainer } = useToast();

  useEffect(() => { api.get('/api/counters/?active=true').then(setCounters); }, []);

  const loadTickets = async () => {
    if (!selectedCounter) return;
    try {
      const t = await api.get(`/api/tickets/counter/${selectedCounter.id}`);
      if (!Array.isArray(t)) return;
      const open = t.find(x => x.status === 'Open');
      setOpenTicket(open || null);
      setTickets(t.filter(x => x.status === 'Waiting'));
    } catch (err) {
      console.error('Counter queue refresh failed:', err);
    }
  };

  // HTTP polling — no Socket.IO
  useEffect(() => {
    if (!selectedCounter) return;
    loadTickets();
    const pollInterval = setInterval(loadTickets, 2000);
    return () => clearInterval(pollInterval);
  }, [selectedCounter]);

  const toggleStatus = async () => {
    try {
      const res = await api.post(`/api/counters/${selectedCounter.id}/status`, { is_open: !selectedCounter.is_open });
      setSelectedCounter(res);
      show(`Counter ${res.is_open ? 'Opened' : 'Closed'}`, 'info');
    } catch (err) {
      show('Failed to update status', 'error');
    }
  };

  const callTicket = async (ticket) => {
    if (!selectedCounter.is_open) {
      show('Please open your counter first', 'error');
      return;
    }
    const shouldOpen = !openTicket || openTicket.id !== ticket.id;
    try {
      setCalling(true);
      if (shouldOpen) {
        setOpenTicket({ ...ticket, status: 'Open' });
        setTickets(prev => prev.filter(t => t.id !== ticket.id));
      }
      await api.post(`/api/tickets/${ticket.id}/call`, { open: shouldOpen });
      show(`Calling ${ticket.ticket_number}…`, 'info');
      loadTickets();
    } catch (err) {
      show(err.error || err.message || 'Call failed', 'error');
      console.error(err);
      loadTickets();
    } finally {
      setCalling(false);
    }
  };

  const skipTicket = async (ticket) => {
    if (!window.confirm(`Skip ticket ${ticket.ticket_number}? This cannot be undone.`)) return;
    await api.post(`/api/tickets/${ticket.id}/skip`, {});
    show(`${ticket.ticket_number} skipped`, 'error');
    loadTickets();
  };

  if (!selectedCounter) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--navy)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 32, padding: 40 }}>
        <div style={{ textAlign: 'center' }}>
          <h1 style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 36, color: '#fff', marginBottom: 8 }}>Counter Login</h1>
          <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 16 }}>Select your counter to begin</p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16, width: '100%', maxWidth: 700 }}>
          {counters.map(c => (
            <button key={c.id} onClick={() => setSelectedCounter(c)}
              style={{
                padding: '32px 24px', borderRadius: 20, border: '2px solid rgba(255,255,255,0.15)',
                background: 'rgba(255,255,255,0.08)', color: '#fff', cursor: 'pointer',
                fontFamily: 'var(--font-display)', transition: 'all 0.2s', textAlign: 'center',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.16)'; e.currentTarget.style.transform = 'translateY(-3px)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.transform = 'none'; }}>
              <div style={{ fontSize: 40, fontWeight: 800, marginBottom: 8 }}>{c.label}</div>
              <div style={{ fontSize: 15, opacity: 0.8, fontWeight: 500 }}>{c.name}</div>
              {c.is_open && <div style={{ fontSize: 10, color: 'var(--green)', marginTop: 8, fontWeight: 700 }}>🟢 OPEN</div>}
            </button>
          ))}
        </div>
      </div>
    );
  }

  const nextTicket = tickets[0];

  return (
    <div style={{ minHeight: '100vh', background: 'var(--off-white)', display: 'flex', flexDirection: 'column' }}>
      <ToastContainer />
      {/* Header */}
      <div style={{ background: 'var(--navy)', padding: '16px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
          <div>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 20, color: '#fff' }}>{selectedCounter.name}</div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>Agent Console · QMS</div>
          </div>
          <button onClick={toggleStatus} style={{
            padding: '8px 20px', borderRadius: 50, border: 'none', 
            background: selectedCounter.is_open ? 'var(--green)' : 'var(--red)', 
            color: '#fff', fontSize: 12, fontWeight: 800, cursor: 'pointer',
            fontFamily: 'var(--font-display)', letterSpacing: '0.05em'
          }}>
            {selectedCounter.is_open ? '● COUNTER OPEN' : '○ COUNTER CLOSED'}
          </button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>Queue</div>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 24, color: 'var(--cobalt-bright)' }}>{tickets.length}</div>
          </div>
          <button onClick={() => setSelectedCounter(null)} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.2)', background: 'transparent', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', fontSize: 12 }}>
            Switch Counter
          </button>
        </div>
      </div>

      <div className="counter-layout" style={{ flex: 1, padding: 28, display: 'grid', gridTemplateColumns: '1fr 380px', gap: 24, maxWidth: 1100, margin: '0 auto', width: '100%' }}>
        {/* Queue list */}
        <div>
          {!selectedCounter.is_open && (
            <div style={{ padding: '20px', background: 'var(--red-light)', color: 'var(--red)', borderRadius: 12, marginBottom: 20, fontWeight: 500, fontSize: 14 }}>
              ⚠️ Your counter is currently closed. You will not receive new tickets and cannot call customers.
            </div>
          )}
          <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 18, color: 'var(--navy)', marginBottom: 16 }}>
            Waiting Queue
            {tickets.length > 0 && <span style={{ marginLeft: 10, fontFamily: 'var(--font-mono)', fontSize: 14, color: 'var(--gray-500)' }}>({tickets.length})</span>}
          </h2>
          {tickets.length === 0 && !openTicket && (
            <div style={{ padding: '60px 40px', textAlign: 'center', background: '#fff', borderRadius: 16, border: '1px solid var(--gray-100)', color: 'var(--gray-400)' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>✓</div>
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 18 }}>Queue is empty</div>
              <div style={{ fontSize: 14, marginTop: 6 }}>No tickets waiting</div>
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, opacity: selectedCounter.is_open ? 1 : 0.6, pointerEvents: selectedCounter.is_open ? 'auto' : 'none' }}>
            {tickets.map((t, i) => (
              <TicketCard key={t.id} ticket={t} isNext={i === 0} isOpen={false}
                onCall={callTicket} onSkip={skipTicket} />
            ))}
          </div>
        </div>

        {/* Right panel */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Active ticket */}
          {openTicket ? (
            <ActiveTicketPanel ticket={openTicket} commentMode={openTicket.comment_mode} onClose={loadTickets} onCall={callTicket} />
          ) : (
            <div style={{ background: 'var(--gray-50)', borderRadius: 20, padding: '28px 24px', textAlign: 'center', border: '2px dashed var(--gray-200)' }}>
              <div style={{ fontSize: 32, marginBottom: 10 }}>⊡</div>
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, color: 'var(--gray-500)', fontSize: 15 }}>No active ticket</div>
              <div style={{ fontSize: 13, color: 'var(--gray-400)', marginTop: 4 }}>Call a ticket from the queue</div>
            </div>
          )}

          {/* Next ticket action */}
          {!openTicket && nextTicket && selectedCounter.is_open && (
            <div style={{ background: '#fff', borderRadius: 16, padding: 20, border: '1px solid var(--gray-100)' }}>
              <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--gray-500)', marginBottom: 12, fontFamily: 'var(--font-display)' }}>Next In Queue</div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 24, fontWeight: 700, color: 'var(--cobalt)', marginBottom: 6 }}>{nextTicket.ticket_number}</div>
              <div style={{ fontSize: 13, color: 'var(--gray-700)', marginBottom: 14 }}>{nextTicket.service_name}</div>
              <Button onClick={() => callTicket(nextTicket)} size="lg" loading={calling} style={{ width: '100%', fontSize: 17, padding: '16px' }}>
                📢 Call Next
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
